import React from "react";
import { motion } from "framer-motion";
import { FaTooth, FaSmile, FaTeeth, FaBrush, FaMicroscope, FaChild, FaHeartbeat, FaGem } from "react-icons/fa";

export default function Services() {
  const services = [
    {
      icon: <FaTooth size={32} />,
      title: "Root Canal Treatment",
      desc: "Advanced and painless root canal treatment to save infected teeth and restore your natural smile."
    },
    {
      icon: <FaGem size={32} />,
      title: "Dental Implants",
      desc: "Permanent and natural-looking dental implants for replacing missing teeth."
    },
    {
      icon: <FaTeeth size={32} />,
      title: "Braces & Aligners",
      desc: "Straighten your teeth with metal braces, ceramic braces and clear aligners."
    },
    {
      icon: <FaSmile size={32} />,
      title: "Hollywood Smile Design",
      desc: "Complete smile makeover with veneers and advanced cosmetic dentistry."
    },
    {
      icon: <FaBrush size={32} />,
      title: "Teeth Whitening",
      desc: "Professional teeth whitening treatment for a brighter and more confident smile."
    },
    {
      icon: <FaHeartbeat size={32} />,
      title: "Cosmetic Dentistry",
      desc: "Smile enhancement with veneers, bonding and aesthetic dental treatments."
    },
    {
      icon: <FaSmile size={32} />,
      title: "Tooth Extraction",
      desc: "Safe and painless tooth extraction with advanced dental techniques."
    },
    {
      icon: <FaChild size={32} />,
      title: "Children's Dentistry",
      desc: "Gentle dental care for children including preventive and restorative treatments."
    },
    {
      icon: <FaMicroscope size={32} />,
      title: "Gum Treatment",
      desc: "Advanced periodontal treatment for healthy gums and long-lasting oral health."
    },
    {
      icon: <FaBrush size={32} />,
      title: "Teeth Cleaning & Polishing",
      desc: "Professional scaling and polishing to remove plaque, tartar and stains."
    },
  ];

  return (
    <section id="services" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-white relative overflow-hidden">
      
      <div className="absolute inset-0">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -right-40 w-80 h-80 bg-teal-100/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-teal-50/40 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-teal-50/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-teal-100"
          >
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
            <span className="text-teal-600 text-xs sm:text-sm font-semibold tracking-wide">OUR SPECIALITIES</span>
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1E293B] mb-4"
          >
            Comprehensive <span className="bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] bg-clip-text text-transparent">Dental Services</span>
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            whileInView={{ opacity: 1, width: "5rem" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="h-1 bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] mx-auto rounded-full"
          />
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-gray-500 text-sm sm:text-base max-w-2xl mx-auto mt-4"
          >
            Dr. Sainulabid's IQRA Multispeciality Dental Clinic offers Root Canal Treatment, Dental Implants, Braces & Aligners, Hollywood Smile Design, Teeth Whitening, Cosmetic Dentistry, Gum Treatment, Tooth Extraction, Children's Dentistry and professional Teeth Cleaning & Polishing for patients from Athavanad, Pattarnadakavu, Thirunavaya, Puthanathani and nearby areas.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="relative p-5 sm:p-6 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-teal-50 to-teal-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                />
                
                <div className="relative z-10">
                  <div className="text-[#2CA6B1] mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300 inline-block">
                    {service.icon}
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-[#1E293B] mb-1.5 sm:mb-2 group-hover:text-teal-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-[#64748B] text-xs sm:text-sm leading-relaxed">
                    {service.desc}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-12 sm:mt-16 max-w-4xl mx-auto text-center"
        >
          <div className="bg-gradient-to-r from-teal-50/50 to-white p-6 sm:p-8 rounded-2xl border border-teal-100 shadow-sm">
            <p className="text-gray-600 text-sm sm:text-base leading-7 sm:leading-8">
              At <strong className="text-teal-600">Dr. Sainulabid's IQRA Multispeciality Dental Clinic</strong>, we are committed to providing high-quality dental care using modern technology and personalized treatment plans. Whether you need preventive, restorative or cosmetic dental treatment, our clinic welcomes patients from <strong className="text-teal-600">Athavanad, Kattilangadi, Kurumbathur, Valanchery, Kuttippuram, Pattarnadakavu, Thirunavaya, Puthanathani</strong> and nearby areas.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}